import React, { useState, useEffect, useMemo } from "react";
import {
  ChevronDown, ChevronRight, Plus, X,
  Calendar, AlertCircle, CheckCircle
} from "lucide-react";
import { CURRENT_YEAR, CURRENT_WBR_WEEK } from "./AdminConstants";

// ——— Toast ———
export function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`admin-toast admin-toast--${type}`}>
      <div className="admin-toast__icon">
        {type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
      </div>
      <span className="admin-toast__message">{message}</span>
      <button className="admin-toast__close" onClick={onClose}>
        <X size={14} />
      </button>
    </div>
  );
}

// ——— Tab Button ———
export function TabButton({ icon: Icon, label, active, onClick }) {
  return (
    <button className={`admin-tab ${active ? "admin-tab--active" : ""}`} onClick={onClick}>
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );
}

// ——— Collapsible Section ———
export function CollapsibleSection({ title, subtitle, defaultOpen = true, children, actions }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="admin-section">
      <div className="admin-section__header" onClick={() => setOpen(!open)}>
        <div className="admin-section__header-left">
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <div>
            <h3 className="admin-section__title">{title}</h3>
            {subtitle && <p className="admin-section__subtitle">{subtitle}</p>}
          </div>
        </div>
        {actions && (
          <div className="admin-section__actions" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
      {open && <div className="admin-section__body">{children}</div>}
    </div>
  );
}

// ——— Form Field ———
export function FormField({ label, required, children, hint }) {
  return (
    <div className="admin-field">
      <label className="admin-field__label">
        {label}
        {required && <span className="admin-field__required">*</span>}
      </label>
      {children}
      {hint && <p className="admin-field__hint">{hint}</p>}
    </div>
  );
}

// ——— Key Name Dialog ———
export function KeyNameDialog({ onConfirm, onCancel, existingKeys }) {
  const [keyName, setKeyName] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    const trimmed = keyName.trim().toLowerCase().replace(/\s+/g, "_");
    if (!trimmed) { setError("Key name is required"); return; }
    if (!/^[a-z0-9_]+$/.test(trimmed)) { setError("Only lowercase letters, numbers, and underscores"); return; }
    if (existingKeys.includes(trimmed)) { setError(`"${trimmed}" already exists`); return; }
    onConfirm(trimmed);
  };

  return (
    <div className="admin-dialog-overlay" onClick={onCancel}>
      <div className="admin-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="admin-dialog__header">
          <h3 className="admin-dialog__title">New POC Entry</h3>
          <button className="admin-btn-icon" onClick={onCancel}><X size={14} /></button>
        </div>
        <div className="admin-dialog__body">
          <FormField label="Key Name" required hint="e.g., cover_page, metrics_review">
            <input
              className={`admin-input ${error ? "admin-input--error" : ""}`}
              value={keyName}
              onChange={(e) => { setKeyName(e.target.value); setError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleConfirm(); if (e.key === "Escape") onCancel(); }}
              placeholder="e.g., cover_page"
              autoFocus
            />
          </FormField>
          {error && <div className="admin-dialog__error"><AlertCircle size={12} /><span>{error}</span></div>}
        </div>
        <div className="admin-dialog__footer">
          <button className="admin-btn admin-btn--outline" onClick={onCancel}>Cancel</button>
          <button className="admin-btn admin-btn--primary" onClick={handleConfirm}><Plus size={14} /> Create</button>
        </div>
      </div>
    </div>
  );
}

// ——— Target Week Selector ———
export function TargetWeekSelector({ selectedYear, setSelectedYear, updateMode, setUpdateMode, singleWeek, setSingleWeek, weekRangeStart, setWeekRangeStart, weekRangeEnd, setWeekRangeEnd, startYear, syncSections, setSyncSections, includeDaterange, setIncludeDaterange }) {
  const yearList = useMemo(() => {
    const years = [];
    for (let y = CURRENT_YEAR; y >= (startYear || 2021); y--) years.push(y);
    return years;
  }, [startYear]);

  const maxWeek = 52; // Always allow full year selection (weeks 1-52)
  const weekList = useMemo(() => Array.from({ length: maxWeek }, (_, i) => i + 1), [maxWeek]);

  return (
    <div className="admin-target-selector">
      <div className="admin-target-selector__header"><Calendar size={18} /><h3>Update Target</h3></div>
      <div className="admin-target-selector__body">
        <div className="admin-target-selector__row">
          <FormField label="Year">
            <select className="admin-input admin-select" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
              {yearList.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </FormField>
          <FormField label="Update Mode">
            <select className="admin-input admin-select" value={updateMode} onChange={(e) => setUpdateMode(e.target.value)}>
              <option value="single">Single Week</option>
              <option value="range">Week Range</option>
              <option value="all">All Weeks in Year</option>
            </select>
          </FormField>
        </div>
        {updateMode === "single" && (
          <div className="admin-target-selector__row">
            <FormField label="Week">
              <select className="admin-input admin-select" value={singleWeek} onChange={(e) => setSingleWeek(Number(e.target.value))}>
                {weekList.map((w) => <option key={w} value={w}>Week {w}</option>)}
              </select>
            </FormField>
          </div>
        )}
        {updateMode === "range" && (
          <div className="admin-target-selector__row">
            <FormField label="From Week">
              <select className="admin-input admin-select" value={weekRangeStart} onChange={(e) => setWeekRangeStart(Number(e.target.value))}>
                {weekList.map((w) => <option key={w} value={w}>Week {w}</option>)}
              </select>
            </FormField>
            <FormField label="To Week">
              <select className="admin-input admin-select" value={weekRangeEnd} onChange={(e) => setWeekRangeEnd(Number(e.target.value))}>
                {weekList.filter((w) => w >= weekRangeStart).map((w) => <option key={w} value={w}>Week {w}</option>)}
              </select>
            </FormField>
          </div>
        )}
        {updateMode === "all" && (
          <div className="admin-target-selector__info"><AlertCircle size={14} /><span>Changes will apply to all weeks in {selectedYear}</span></div>
        )}
        {/* Selective Sync — shown for multi-week modes */}
        {updateMode !== "single" && syncSections && (
          <div className="admin-target-selector__sync">
            <div className="admin-target-selector__sync-header">
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#565959" }}>Sections to Sync</span>
            </div>
            <div className="admin-target-selector__sync-options" style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer" }}>
                <input type="checkbox" checked={syncSections.details} onChange={(e) => setSyncSections({ ...syncSections, details: e.target.checked })} />
                Function Details
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer" }}>
                <input type="checkbox" checked={syncSections.sidebar} onChange={(e) => setSyncSections({ ...syncSections, sidebar: e.target.checked })} />
                Sidebar Config
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer" }}>
                <input type="checkbox" checked={syncSections.poc} onChange={(e) => setSyncSections({ ...syncSections, poc: e.target.checked })} />
                POC Management
              </label>
            </div>
            <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e8e8e8" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer", color: "#866118" }}>
                <input type="checkbox" checked={includeDaterange} onChange={(e) => setIncludeDaterange(e.target.checked)} />
                Include Date Range (⚠️ overwrites each week's date range)
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
